// ============================================================================
// ui.js — the ui() cue: UI / CSS state changes
// -----------------------------------------------------------------------------
// Supports declarative UI state changes via:
//   ui(selector, opacity:0.5, scale:1.2, x:10, y:-20, rotate:15, visible:false, dur:0.3)
//
// target: a CSS selector (any element); uid: an element of the score.
// Uses whitelisted properties only.
// Keeps internal transform state per element.
// ============================================================================

import { hideElement, showElement, toggleElement } from './animShared.js';
import { bindParam, isSignalRef, isOscRef } from '../control/paramBinding.js';
import { get as busGet } from '../control/paramBus.js';
import { runNext } from './cueNext.js';
import { seekAnimation } from './animSync.js';
import { secs } from './beats.js';
import { later } from './sequence.js';

// ----------------------------------------------------------------------------
// ui(uid:<fader>, value:<0-1>, dur:<beats>) — move a touch fader (an o2p with
// trig:touch) to a value, gliding over dur, exactly as if it were dragged: the
// handle moves, everything bound to it follows, and other clients see it.
// value:restore glides back to where it was before the last value: — so a
// score can stop a bound motion (a bipolar fader at 0.5) and later give it
// back whatever speed the performer had set.
// ----------------------------------------------------------------------------
const faderHistory = new Map();   // uid -> [previous values], the last FADER_HISTORY_MAX
const FADER_HISTORY_MAX = 16;
const faderGlides = new Map();    // uid -> running glide token

const bare = (v) => String(v).replace(/^["'`]+|["'`]+$/g, "");

/** Does a bipolar motion (o2p bipolar:1) take its speed from this fader? */
function drivesBipolar(uid) {
  return Object.values(window.oscillaAnimRegistry || {}).some(({ cfg }) =>
    cfg?.bipolar && cfg._durRaw?.source === uid);
}

/**
 * ui(uid:<fader>, step:0.05) — a step on a fader, relative to where it is.
 * On a fader that sets a bipolar speed the step is measured from the centre:
 * + is faster in whichever direction the motion goes, − slower, stopping at
 * the centre rather than crossing it; from rest + goes forward. Otherwise
 * the step is literal (+ moves the handle right).
 */
function stepTarget(uid, now, step) {
  if (!drivesBipolar(uid)) return now + step;
  if (Math.abs(now - 0.5) < 1e-6) return step > 0 ? 0.5 + step : 0.5;
  return now > 0.5 ? Math.max(0.5, now + step) : Math.min(0.5, now - step);
}

function setFaderValue(uid, value, dur = 0, { step: by = null } = {}) {
  const fader = window.oscillaFaders?.get(uid);
  if (!fader) { console.warn(`[ui] value: — no touch fader with uid ${uid}`); return; }
  // A glide still under way counts as arrived: a step or a hold that follows
  // at once starts from where the fader is going, not from mid-glide
  const going = faderGlides.get(uid);
  const now = going && !going.cancel && !going.landed ? going.target : Number(busGet(`${uid}.t`, NaN));
  const word = value == null ? null : bare(value);
  let target;
  if (by != null) {
    if (!Number.isFinite(now) || !Number.isFinite(Number(by))) return;
    target = Math.min(1, Math.max(0, stepTarget(uid, now, Number(by))));
  } else if (word === "flip") {
    // t → 1−t: a bipolar speed reverses, a 0/1 switch toggles
    if (!Number.isFinite(now)) return;
    target = 1 - now;
  } else if (word === "hold" || word === "rest" || word === "go") {
    // hold: stop at the centre, keeping the speed it had for everyone, so
    // whoever holds next (any client) gives it back. rest only stops, go
    // only gives back — for a bar that stops or starts several at once.
    if (!Number.isFinite(now)) return;
    if (Math.abs(now - 0.5) < 1e-3) {
      if (word === "rest" || fader.held == null) return;
      target = fader.held;
      fader.held = null;
    } else {
      if (word === "go") return;
      fader.held = now;
      target = 0.5;
    }
  } else if (word === "restore") {
    const stack = faderHistory.get(uid) || [];
    if (!stack.length) return;
    target = stack.pop();
  } else {
    target = Math.min(1, Math.max(0, Number(value)));
    if (!Number.isFinite(target)) { console.warn(`[ui] value:${value} is not a number`); return; }
    if (Number.isFinite(now)) {
      const stack = faderHistory.get(uid) || [];
      stack.push(now);
      if (stack.length > FADER_HISTORY_MAX) stack.shift();
      faderHistory.set(uid, stack);
    }
  }
  const prev = faderGlides.get(uid);
  if (prev) prev.cancel = true;
  const token = { cancel: false, target, landed: false };
  faderGlides.set(uid, token);
  // the bus drops a publish that follows another within a frame: say the
  // final value again a moment later so it cannot be lost (a stop must be 0.5)
  const land = () => {
    fader.set(target);
    setTimeout(() => { if (!token.cancel) { fader.set(target); token.landed = true; } }, 40);
  };
  const at = Number(busGet(`${uid}.t`, NaN));    // where the handle is now
  const from = Number.isFinite(at) ? at : target;
  if (!(dur > 0) || from === target) { land(); return; }
  const t0 = performance.now();
  const step = (ts) => {
    if (token.cancel) return;
    const k = Math.min(1, (ts - t0) / (dur * 1000));
    const e = -(Math.cos(Math.PI * k) - 1) / 2;
    if (k < 1) { fader.set(from + (target - from) * e); requestAnimationFrame(step); }
    else land();
  };
  requestAnimationFrame(step);
}

/**
 * A ui() number, with follow() allowed.
 *
 * SAMPLED at trigger, like fade(). A ui() cue is a command — "put this element
 * at opacity 0.5 over 0.3s" — carried out by a CSS transition. A value that
 * kept changing would be writing the same property the transition is ramping,
 * and the two would fight. So the signal is read when the cue fires.
 *
 * Ranges are per property: opacity is 0-1, a scale factor is not, and x/y are
 * pixels. Without the right range a follow() with no numbers of its own would
 * map everything into 0-1 and silently flatten the cue.
 */
const UI_RANGE = {
  opacity: [0, 1], scale: [0, 4], rotate: [0, 360], x: [-500, 500], y: [-500, 500],
  dur: [0, 10],   // beats; without this a bound dur would cap at the 0-1 default
};

function uiNumber(prop, value) {
  if (!isSignalRef(value) && !isOscRef(value)) return value;
  const [lo, hi] = UI_RANGE[prop] ?? [0, 1];
  const b = bindParam(value, () => {}, { min: lo, max: hi });
  b?.unbind?.();                            // one reading, then let go
  return Number.isFinite(b?.value) ? b.value : 0;
}

const uiState = new WeakMap();

/**
 * Whitelisted UI properties
 * (do NOT allow arbitrary CSS injection)
 */
const UI_PROPS = new Set([
  "opacity",
  "scale",
  "x",
  "y",
  "rotate",
  "visible",
  "color",
  "bg",
  "z",
  "action",
]);

/**
 * handleUICue(ast)
 * ---------------------------------------------------------------------------
 * AST format expected:
 * {
 *   type: "cueUi",
 *   args: [
 *     { type: "selector", value: "#playhead" },
 *     { type: "opacity", value: 0 },
 *     { type: "dur", value: 0.3 }
 *   ]
 * }
 */
export function handleUICue(ast, sourceEl = null) {

  // ----------------------------------
  // Action mode (control-plane bridge) - only for controlXY actions
  // ----------------------------------
  const actionArg = ast.args?.find(a => a.type === "action");
  const controlPlaneActions = ["controlXYSave", "controlXYRecall", "controlXYDefineSequence", "controlXYSequence", "controlXYSequenceStop"];
  if (actionArg && controlPlaneActions.includes(actionArg.value)) {
    handleUIAction(ast.args);
    return;
  }

  // ----------------------------------
  // Regular UI mode (requires selector or uid)
  // ----------------------------------
  if (!ast || !Array.isArray(ast.args)) {
    console.warn("[ui] Invalid AST:", ast);
    return;
  }

  let selector = null;
  let uid = null;
  let dur = 0;
  const params = {};

  // ----------------------------------
  // Extract selector/uid + params
  // ----------------------------------
  for (const arg of ast.args) {
    if (!arg || !arg.type) continue;

    if (arg.type === "selector" || arg.type === "target") {
      selector = arg.value;
      continue;
    }

    if (arg.type === "uid") {
      uid = arg.value;
      continue;
    }

    if (arg.type === "dur") {
      dur = Number(secs(uiNumber("dur", arg.value))) || 0;   // beats -> seconds
      continue;
    }

    if (UI_PROPS.has(arg.type)) {
      params[arg.type] = uiNumber(arg.type, arg.value);
    }
  }

  // next: — the following cue, once this one's dur has run (cueNext.js):
  // ui(uid:L1, phase:0.2, next:"ui(uid:spd1, value:0.62)") seeks, then sets the speed
  const next = ast.args.find(a => a?.type === "next")?.value;
  if (next != null && (uid || selector)) later(() => runNext(next, sourceEl), dur * 1000);

  // value: / step: — move a fader (see setFaderValue); phase: — seek a
  // running motion. uid may be a list; a list of values pairs with it.
  const valueArg = ast.args.find(a => a?.type === "value");
  const stepArg = ast.args.find(a => a?.type === "step");
  const phaseArg = ast.args.find(a => a?.type === "phase");
  if (uid && (valueArg || stepArg || phaseArg)) {
    const uids = Array.isArray(uid) ? uid.map(String) : [String(uid)];
    const nth = (v, i) => Array.isArray(v) ? v[Math.min(i, v.length - 1)] : v;
    uids.forEach((u, i) => {
      if (phaseArg) seekAnimation(u, Number(nth(phaseArg.value, i)));
      if (stepArg) setFaderValue(u, null, dur, { step: nth(stepArg.value, i) });
      else if (valueArg) setFaderValue(u, nth(valueArg.value, i), dur);
    });
    return;
  }

  // ----------------------------------
  // UID mode - target SVG element + overlays by animation uid
  // ----------------------------------
  if (uid) {
    // Toggle action
    if (params.action === "toggle") {
      toggleElement(uid, { dur, sourceEl });
      return;
    }

    // Explicit visible true/false
    if ("visible" in params) {
      const visible = !!params.visible;
      if (visible) {
        // Placing the shown element beside its source is for a clicked button
        // opening a panel. A chained or playhead-fired reveal (next:, a marker)
        // shows the element where it is — its "source" is only a cue holder,
        // and moving a constellation to it would send it off the map.
        const clicked = (ast.args || []).some(a =>
          (a.type === "click" || a.type === "drag-click") && [1, "1", true, "true"].includes(a.value));
        showElement(uid, { dur, sourceEl: clicked ? sourceEl : null });
      } else {
        hideElement(uid, { dur });
      }
    }
    // Could add opacity/scale support for uid mode later
    return;
  }

  if (!selector) {
    // No uid/selector — fire nested trigger if present (e.g. ui(click:1, trigger:stopwatch(...)))
    if (ast.triggerAst) {
      import("./cueDispatcher.js").then(({ handleCueTrigger }) => {
        handleCueTrigger(ast.triggerAst, false, true, sourceEl);
      });
    } else {
      console.warn("[ui] Missing selector or uid (required for UI manipulation):", ast);
    }
    return;
  }

  const elements = document.querySelectorAll(selector);
  if (!elements.length) {
    console.warn(`[ui] No elements match selector: ${selector}`);
    return;
  }

  elements.forEach(el => applyUiToElement(el, params, dur));
}


function handleUIAction(args) {
  const params = {};
  for (const a of args) {
    if (!a?.type) continue;
    params[a.type] = a.value;
  }

  const action = params.action;
  if (!action) {
    console.warn("[ui] action missing");
    return;
  }

  switch (action) {
    case "controlXYSave":
      window.controlXYPresets?.save(
        params.preset,
        params.uid
      );
      break;

    case "controlXYRecall":
      window.controlXYPresets?.recall(
        params.preset,
        {
          dur: secs(params.dur),
          ease: params.ease
        }
      );
      break;

    case "controlXYDefineSequence":
      {
        const name = params.name || params.seq || params.sequence;
        if (!name) {
          console.warn("[ui] controlXYDefineSequence: name required");
          return;
        }
        
        // Parse steps from comma-separated string or array
        let steps;
        if (typeof params.steps === 'string') {
          steps = params.steps.split(',').map(s => s.trim()).filter(Boolean);
        } else if (Array.isArray(params.steps)) {
          steps = params.steps;
        } else {
          console.warn("[ui] controlXYDefineSequence: steps required (comma-separated string or array)");
          return;
        }
        
        if (steps.length === 0) {
          console.warn("[ui] controlXYDefineSequence: empty steps");
          return;
        }
        
        window.controlXYPresets?.defineSequence(name, steps);
      }
      break;

    case "controlXYSequence":
      window.controlXYPresets?.playSequence(
        params.seq || params.sequence,
        {
          dur: secs(params.dur),
          ease: params.ease,
          loop: params.loop
        }
      );
      break;

    case "controlXYSequenceStop":
      window.controlXYPresets?.stopSequence();
      break;

    default:
      console.warn("[ui] Unknown action:", action);
  }
}



/**
 * Apply UI params to a single element
 */
function applyUiToElement(el, params, dur) {
  // action:toggle flips visibility here as well as in uid mode:
  // ui(target:"#some-panel", action:toggle)
  if (params.action === "toggle") {
    params.visible = getComputedStyle(el).display === "none";
  }

  // ----------------------------------
  // Initialise transform state
  // ----------------------------------
  if (!uiState.has(el)) {
    uiState.set(el, {
      scale: 1,
      x: 0,
      y: 0,
      rotate: 0,
    });
  }

  const state = uiState.get(el);

  // ----------------------------------
  // Transition handling
  // ----------------------------------
  if (dur > 0) {
    el.style.transition = [
      `opacity ${dur}s ease`,
      `transform ${dur}s ease`,
      `background-color ${dur}s ease`,
      `color ${dur}s ease`
    ].join(", ");
  } else {
    el.style.transition = "none";
  }

  // ----------------------------------
  // Visibility (display + pointer events)
  // ----------------------------------
  if ("visible" in params) {
    const visible = !!params.visible;
    // A hide still fading out is called off by what comes after it, or a
    // show within its dur: would be hidden again when the old timer fires
    // (the same slot as setElementVisibility's, animShared.js)
    clearTimeout(el._oscillaHideTimer);
    el._oscillaHideTimer = null;

    if (!visible) {
      el.style.pointerEvents = "none";

      if (dur > 0) {
        el.style.opacity = "0";
        el._oscillaHideTimer = setTimeout(() => {
          el._oscillaHideTimer = null;
          el.style.display = "none";
        }, dur * 1000);
      } else {
        el.style.display = "none";
      }
    } else {
      el.style.display = "";
      el.style.pointerEvents = "auto";
      el.style.opacity = "1";
    }
  }

  // ----------------------------------
  // Opacity
  // ----------------------------------
  if ("opacity" in params) {
    el.style.opacity = String(params.opacity);
  }

  // ----------------------------------
  // Colour
  // ----------------------------------
  if ("color" in params) {
    el.style.color = params.color;
  }

  if ("bg" in params) {
    el.style.backgroundColor = params.bg;
  }

  // ----------------------------------
  // Z-index
  // ----------------------------------
  if ("z" in params) {
    el.style.zIndex = String(params.z);
  }

  // ----------------------------------
  // Transform state update
  // ----------------------------------
  if ("scale" in params) state.scale = Number(params.scale);
  if ("x" in params) state.x = Number(params.x);
  if ("y" in params) state.y = Number(params.y);
  if ("rotate" in params) state.rotate = Number(params.rotate);

  el.style.transform = `
    translate(${state.x}px, ${state.y}px)
    scale(${state.scale})
    rotate(${state.rotate}deg)
  `.trim();
}